import { Link, Navigate, useParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { LEARN_POSTS_BY_SLUG, AUTHORS_BY_SLUG } from '../../data/seoRetrofitData';
import QuoteLeadForm from '../../components/QuoteLeadForm';

export default function LearnArticlePage() {
  const { slug } = useParams();
  const post = LEARN_POSTS_BY_SLUG[slug];
  if (!post) return <Navigate to="/learn" replace />;

  const author = AUTHORS_BY_SLUG[post.authorSlug];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    ...(post.published ? { datePublished: post.published } : {}),
    ...(post.updated ? { dateModified: post.updated } : {}),
    author: {
      '@type': 'Person',
      name: author?.name || 'Andrew Sunna',
      url: `https://www.flashfunding.loan/authors/${post.authorSlug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Flash Funding',
      url: 'https://www.flashfunding.loan',
    },
  };

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Learn', path: '/learn' },
    { name: post.title, path: `/learn/${post.slug}` },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="learn-article-page">
      <SEOHead
        title={post.metaTitle}
        description={post.metaDescription}
        canonical={`/learn/${post.slug}`}
        keywords={`${post.targetKeyword}, asset finance australia, flash funding learn`}
        breadcrumbs={breadcrumbs}
        faqs={post.faqs.map((faq) => ({ q: faq.q, a: faq.a }))}
        schema={[articleSchema]}
        noindex={post.noindex}
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-10" data-testid="learn-article-hero-section">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight mb-4" data-testid="learn-article-h1">{post.title}</h1>
        {post.updated && (
          <p className="text-white/65 text-sm mb-3" data-testid="learn-article-meta-line">
            Updated {new Date(post.updated).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        )}
        <p className="text-white/75 text-sm" data-testid="learn-article-parent-link-intro">
          Related: <Link to={post.pillarPath} className="text-purple-300 hover:text-purple-200" data-testid="learn-article-parent-pillar-link">{post.internalLinks[0].label}</Link>
        </p>

        <div className="mt-4 rounded-xl border border-white/10 bg-navy-800/70 p-4" data-testid="learn-article-author-card">
          <div className="flex items-center gap-3">
          {author?.photo && <img src={author.photo} alt={author.name} className="w-12 h-12 rounded-full object-cover" />}
          <div>
          <p className="text-xs uppercase tracking-[0.14em] text-white/55">Author</p>
          <Link to={`/authors/${post.authorSlug}`} className="text-white font-semibold hover:text-purple-300" data-testid="learn-article-author-link">
            {author?.name || 'Andrew Sunna'}
          </Link>
          {author?.credentials && <p className="text-xs text-white/60 mt-1">{author.credentials}</p>}
          </div>
          </div>
        </div>
      </section>

      {post.sections.map((section, index) => (
        <section key={section.heading} className={`py-8 ${index % 2 === 0 ? 'bg-navy-900/70' : 'bg-navy-800/70'}`} data-testid={`learn-article-section-${index + 1}`}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-4" data-testid={`learn-article-section-heading-${index + 1}`}>{section.heading}</h2>
            {section.content.split('\n\n').map((block, pIndex) => (
              block.startsWith('- ') ? (
                <ul key={pIndex} className="list-disc pl-5 space-y-2 text-white/75 text-sm leading-relaxed mb-4" data-testid={`learn-article-section-list-${index + 1}-${pIndex + 1}`}>
                  {block.split('\n').map((item) => <li key={item}>{item.replace(/^- /, '')}</li>)}
                </ul>
              ) : (
                <p key={pIndex} className="text-white/75 text-sm leading-relaxed mb-4" data-testid={`learn-article-section-paragraph-${index + 1}-${pIndex + 1}`}>{block}</p>
              )
            ))}
          </div>
        </section>
      ))}

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="learn-article-links-section">
        {post.faqs.length > 0 && (
          <div className="mb-10" data-testid="learn-article-faq-section">
            <h2 className="text-2xl font-bold text-white mb-4">Frequently asked questions</h2>
            {post.faqs.map((faq) => (
              <div key={faq.q} className="mb-4">
                <h3 className="text-base font-semibold text-white mb-1">{faq.q}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        )}
        <h2 className="text-2xl font-bold text-white mb-4">Related reading</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="learn-article-links-grid">
          {post.internalLinks.map((item) => (
            <Link key={item.path} to={item.path} className="text-sm text-purple-300 hover:text-purple-200" data-testid={`learn-article-internal-link-${item.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              {item.label}
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="learn-article-quote-form-section">
        <div className="rounded-2xl border border-purple-500/25 bg-navy-800/70 p-5 sm:p-6">
          <h2 className="text-3xl font-bold text-white mb-3">Ready to compare lender options?</h2>
          <p className="text-sm text-white/70 mb-5">Use the quote form and we’ll match your scenario to the right lender path.</p>
          <QuoteLeadForm sourcePage={`/learn/${post.slug}`} dataTestIdPrefix={`learn-quote-form-${post.slug}`} />
        </div>
      </section>
    </div>
  );
}
